import _ from 'lodash';

import { getUUID } from 'utils/uuid';

import {
  DisabledReasonHint,
  DisabledReasonTitle,
  HIDDEN_REASONS_SUFFIX,
  MAX_VISIBLE_DISABLED_REASONS,
  OtherAuthorsLabel,
  PENDING_CHANGES_AUTHORS_TITLE_PREFIX,
  TopButtonLabel,
} from './SavePendingChangesButton.constants';

// When granular permissions deny Save, the "Save" affordance must never present or trigger a save — it is gated
// strictly on the save grant. A user without save permission never sees the Save split-button; they get a single
// standalone action instead (Cancel when cancel is granted, otherwise the read-only "See Changes").
export const getIsSavePendingChangesSaveDenied = ({
  isSavePendingChangesLoanActionEnabled,
  isSaveGranted,
}) => !!isSavePendingChangesLoanActionEnabled && !isSaveGranted;

export const getSavePendingChangesTopButtonLabel = ({ isViewButtonOnTop }) =>
  isViewButtonOnTop ? TopButtonLabel.VIEW : TopButtonLabel.SAVE;

export const SAVE_PERMISSION_DENIED_REASON = {
  title: DisabledReasonTitle.NO_SAVE_ACCESS,
  reasons: [],
  hint: null,
};

export const getSaveDisabledTooltip = ({
  isSaveDenied,
  permissionTooltip,
  isDisabled,
  disabledTooltip,
}) => {
  if (isSaveDenied) {
    return permissionTooltip;
  }

  if (isDisabled) {
    return disabledTooltip;
  }

  return '';
};

const getVisibleReasons = reasons => {
  const uniqueReasons = _.uniq(_.compact(reasons));
  const hiddenCount = uniqueReasons.length - MAX_VISIBLE_DISABLED_REASONS;
  const visibleReasons =
    hiddenCount > 0
      ? [
          ...uniqueReasons.slice(0, MAX_VISIBLE_DISABLED_REASONS),
          `+${hiddenCount} ${HIDDEN_REASONS_SUFFIX}`,
        ]
      : uniqueReasons;

  return visibleReasons.map(text => ({ id: getUUID(), text }));
};

const getAuthorsLabel = authorNames => {
  const names = _.compact(authorNames);

  if (_.isEmpty(names)) {
    return null;
  }

  const [firstAuthorName, ...otherAuthorNames] = names;

  if (_.isEmpty(otherAuthorNames)) {
    return firstAuthorName;
  }

  const othersWord =
    otherAuthorNames.length === 1 ? OtherAuthorsLabel.SINGULAR : OtherAuthorsLabel.PLURAL;

  return `${firstAuthorName} and ${otherAuthorNames.length} ${othersWord}`;
};

const getNoSaveAccessReason = pendingChangesAuthors => {
  const authorsLabel = getAuthorsLabel(pendingChangesAuthors);

  return authorsLabel
    ? {
        title: `${PENDING_CHANGES_AUTHORS_TITLE_PREFIX} ${authorsLabel}`,
        reasons: [],
        hint: DisabledReasonHint.NO_SAVE_ACCESS,
      }
    : {
        title: DisabledReasonTitle.NO_SAVE_ACCESS,
        reasons: [],
        hint: null,
      };
};

export const getSaveDisabledReason = ({
  isLoading,
  isLockPending,
  isLoanLocked,
  isProgramChangeAllowed,
  isUpdateRequestPending,
  isViewDisabled,
  isEditChangesDisabled,
  programChangeReasons,
  pendingChangesAuthors,
  hasNoOwnChanges = false,
}) => {
  if (isLoading) {
    return null;
  }

  if (isLockPending) {
    return {
      title: DisabledReasonTitle.LOCK_PENDING,
      reasons: [],
      hint: null,
    };
  }

  if (isLoanLocked && !isProgramChangeAllowed) {
    return {
      title: DisabledReasonTitle.PROGRAM_CHANGE_NOT_ALLOWED,
      reasons: getVisibleReasons(programChangeReasons),
      hint: DisabledReasonHint.OPTIMAL_BLUE_ADMIN,
    };
  }

  if (isUpdateRequestPending) {
    return {
      title: DisabledReasonTitle.UPDATE_REQUEST_PENDING,
      reasons: [],
      hint: null,
    };
  }

  if (isViewDisabled || isEditChangesDisabled) {
    return getNoSaveAccessReason(pendingChangesAuthors);
  }

  if (hasNoOwnChanges) {
    return {
      title: DisabledReasonTitle.NO_OWN_CHANGES,
      reasons: [],
      hint: null,
    };
  }

  return null;
};

/**
 * Menu state when pending changes are shown by user: "my" actions stay gated by the loan action
 * grants, while the selected users' actions additionally need the save-other-users claim.
 */
export const getPendingChangesByUserMenuState = ({
  isBusy,
  isSaveBlocked,
  isCancelBlocked,
  isLoanActionEnabled,
  isSaveGranted,
  isCancelGranted,
  hasOwnChanges,
  hasOthersSelected,
  isOthersSaveAllowed,
}) => {
  const isLoanActionSaveDenied = !!isLoanActionEnabled && !isSaveGranted;
  const isLoanActionCancelDenied = !!isLoanActionEnabled && !isCancelGranted;

  return {
    isSaveMineHidden: isLoanActionSaveDenied,
    isSaveMineDisabled: isBusy || isSaveBlocked || !hasOwnChanges,
    isCancelMineHidden: isLoanActionCancelDenied,
    isCancelMineDisabled: isBusy || isCancelBlocked || !hasOwnChanges,
    isSaveSelectedHidden: !hasOthersSelected || isLoanActionSaveDenied,
    isSaveSelectedDisabled: !isOthersSaveAllowed || isBusy || isSaveBlocked,
    isCancelSelectedHidden: !hasOthersSelected || isLoanActionCancelDenied,
    isCancelSelectedDisabled: !isOthersSaveAllowed || isBusy || isCancelBlocked,
    isOthersPermissionMessageShown: hasOthersSelected && !isOthersSaveAllowed,
  };
};
